import type { Bathymetry } from '@core/map/bathymetry';
import { toWorld, toLatLon } from '@core/geo';

/**
 * Erzeugt aus dem Hoehenraster die Kartentextur der Weltkarte.
 *
 * Zwei Dinge passieren hier gleichzeitig:
 *
 * 1. **Umprojektion.** Das Raster liegt equirektangular (gleiche Gradabstaende),
 *    die Spielwelt ist Mercator. Weil Mercator ausschliesslich in der Hoehe
 *    verzerrt, bleibt die x-Achse 1:1 — es genuegt eine vertikale Neuabtastung
 *    statt einer vollen Bildtransformation.
 *
 * 2. **Einfaerbung.** Tiefenabhaengige Farbverlaeufe plus Hoehenlinien an den
 *    klassischen Marken. Das erzeugt den Seekarten-Eindruck, ohne dass dafuer
 *    Grafik-Assets noetig waeren.
 */

/**
 * Farbstufen fuer Wasser, nach Tiefe in Metern (positiv).
 *
 * Die Tiefsee bleibt dunkel und kalt, laeuft aber bewusst nicht gegen Schwarz:
 * Rund 90 Prozent der Karte liegen tiefer als 1.000 m, und in der Uebersicht
 * bestimmt genau dieser Bereich das Bild. Waere er schwarz, waere die Seekarte
 * dort nicht mehr zu lesen — duester soll sie sein, nicht blind.
 */
const WATER_STOPS: readonly (readonly [number, readonly [number, number, number]])[] = [
  [0, [58, 130, 156]],
  [50, [42, 104, 130]],
  [200, [32, 82, 108]],
  [1000, [24, 62, 86]],
  [2000, [19, 50, 71]],
  [4000, [14, 39, 57]],
  [6000, [10, 30, 45]],
  [9000, [7, 22, 34]],
];

/** Farbstufen fuer Land, nach Hoehe in Metern. */
const LAND_STOPS: readonly (readonly [number, readonly [number, number, number]])[] = [
  [0, [66, 61, 53]],
  [500, [82, 75, 64]],
  [2000, [104, 96, 82]],
  [3500, [124, 115, 98]],
];

/**
 * Reliefschattierung.
 *
 * Aus demselben Hoehenraster, das schon die Farbe bestimmt, laesst sich eine
 * Schummerung berechnen — und die macht aus einer Farbflaeche eine Landschaft:
 * Die Kante der Bahama-Bank, der Kontinentalhang, der Puerto-Rico-Graben und
 * der Bermuda-Seeberg treten plastisch hervor.
 *
 * Licht von Nordwesten, wie in der Kartografie ueblich. Das Auge liest eine
 * so beleuchtete Flaeche als erhaben; von Suedosten beleuchtet kippt der
 * Eindruck und Gruben wirken wie Huegel.
 */
const LIGHT = (() => {
  const [x, y, z] = [-1, -1, 1.15];
  const length = Math.hypot(x, y, z);
  return { x: x / length, y: y / length, z: z / length };
})();

/** Kantenlaenge einer Rasterzelle in Metern (eine Bogenminute ≈ eine Seemeile). */
const CELL_METRES = 1852;

/**
 * Ueberhoehung der Hoehenunterschiede.
 *
 * Ohne sie waere die Schummerung unsichtbar: Auf 1.852 m Zellbreite kommen
 * selbst am Kontinentalhang nur wenige hundert Meter Hoehenunterschied, die
 * Neigung liegt also im Bereich weniger Grad.
 */
const RELIEF_EXAGGERATION = 14;

/** Tiefen, an denen eine Hoehenlinie gezogen wird. */
const CONTOUR_DEPTHS = [200, 1000, 2000, 4000, 6000] as const;

/**
 * Wie stark eine Hoehenlinie das Grundbild aufhellt.
 *
 * Zurueckhaltend gewaehlt: Die Linien sind einen Rasterpunkt breit und werden
 * beim Hineinzoomen mitvergroessert. Zu kraeftig gesetzt dominieren sie im
 * Nahbereich das Bild als Treppenmuster.
 */
const CONTOUR_LIGHTEN = 0.22;

function sampleStops(
  stops: readonly (readonly [number, readonly [number, number, number]])[],
  value: number,
): readonly [number, number, number] {
  const first = stops[0] as readonly [number, readonly [number, number, number]];
  if (value <= first[0]) return first[1];

  for (let i = 1; i < stops.length; i++) {
    const [upper, upperColor] = stops[i] as readonly [number, readonly [number, number, number]];
    if (value <= upper) {
      const [lower, lowerColor] = stops[i - 1] as readonly [
        number,
        readonly [number, number, number],
      ];
      const t = (value - lower) / (upper - lower);
      return [
        lowerColor[0] + (upperColor[0] - lowerColor[0]) * t,
        lowerColor[1] + (upperColor[1] - lowerColor[1]) * t,
        lowerColor[2] + (upperColor[2] - lowerColor[2]) * t,
      ];
    }
  }

  return (stops[stops.length - 1] as readonly [number, readonly [number, number, number]])[1];
}

function colourFor(elevation: number): readonly [number, number, number] {
  return elevation > 0
    ? sampleStops(LAND_STOPS, elevation)
    : sampleStops(WATER_STOPS, -elevation);
}

/**
 * Beleuchtungsfaktor an einem Rasterpunkt, 0 (abgewandt) bis 1 (zugewandt).
 * Zentrale Differenzen ueber die Nachbarpunkte ergeben die Flaechennormale.
 */
function reliefAt(bathy: Bathymetry, x: number, y: number): number {
  const dzdx =
    ((bathy.elevationAtIndex(x + 1, y) - bathy.elevationAtIndex(x - 1, y)) /
      (2 * CELL_METRES)) *
    RELIEF_EXAGGERATION;
  const dzdy =
    ((bathy.elevationAtIndex(x, y + 1) - bathy.elevationAtIndex(x, y - 1)) /
      (2 * CELL_METRES)) *
    RELIEF_EXAGGERATION;

  const length = Math.hypot(dzdx, dzdy, 1);
  const nx = -dzdx / length;
  const ny = -dzdy / length;
  const nz = 1 / length;

  return Math.max(0, nx * LIGHT.x + ny * LIGHT.y + nz * LIGHT.z);
}

/** Liegt zwischen zwei benachbarten Hoehen eine Hoehenlinie? */
function crossesContour(a: number, b: number): boolean {
  if (a === b) return false;
  const lo = Math.min(a, b);
  const hi = Math.max(a, b);

  // Kuestenlinie wird separat als Vektor gezeichnet, hier nur Tiefenlinien.
  for (const depth of CONTOUR_DEPTHS) {
    const level = -depth;
    if (lo < level && hi >= level) return true;
  }
  return false;
}

export interface MapTexture {
  /** Tiefenverlauf, deckend. */
  readonly base: HTMLCanvasElement;
  /**
   * Tiefenlinien auf eigener, sonst durchsichtiger Ebene.
   *
   * Sie sind einen Rasterpunkt breit und werden beim Hineinzoomen mit
   * vergroessert — im Nahbereich wuerden daraus Treppenmuster, die das Bild
   * beherrschen. Als eigene Ebene lassen sie sich dort einfach ausblenden,
   * ohne den Seekarten-Eindruck in der Uebersicht zu verlieren.
   */
  readonly contours: HTMLCanvasElement;
  /** Rechteck in Weltkoordinaten (Seemeilen), das die Textur abdeckt. */
  readonly world: { x: number; y: number; width: number; height: number };
}

/**
 * Baut die Textur. Laeuft einmal beim Laden; bei rund 1,3 Millionen Bildpunkten
 * dauert das je nach Geraet einige hundert Millisekunden.
 */
export function buildMapTexture(bathy: Bathymetry): MapTexture {
  const { north, south, west, east } = bathy.meta.bounds;

  const topLeft = toWorld({ lat: north, lon: west });
  const bottomRight = toWorld({ lat: south, lon: east });

  const worldWidth = bottomRight.x - topLeft.x;
  const worldHeight = bottomRight.y - topLeft.y;

  // Rund ein Bildpunkt je Seemeile. In der Breite entspricht das genau dem
  // Quellraster (eine Bogenminute), sodass x ohne Interpolation abbildet.
  const width = bathy.width;
  const height = Math.max(1, Math.round(worldHeight));

  const makeCanvas = (): [HTMLCanvasElement, CanvasRenderingContext2D] => {
    const element = document.createElement('canvas');
    element.width = width;
    element.height = height;
    const ctx = element.getContext('2d', { willReadFrequently: false });
    if (ctx === null) {
      throw new Error('buildMapTexture: 2D-Kontext nicht verfügbar');
    }
    return [element, ctx];
  };

  const [baseCanvas, baseContext] = makeCanvas();
  const [contourCanvas, contourContext] = makeCanvas();

  const baseImage = baseContext.createImageData(width, height);
  const pixels = baseImage.data;
  const contourImage = contourContext.createImageData(width, height);
  const contourPixels = contourImage.data;

  // Zeilenweise arbeiten: erst die Hoehen der Zeile bestimmen, dann einfaerben.
  // Die vorige Zeile bleibt erhalten, damit waagerechte Hoehenlinien erkennbar sind.
  let previousRow = new Float32Array(width);
  let currentRow = new Float32Array(width);

  const gridHeight = bathy.height;

  for (let y = 0; y < height; y++) {
    // Bildzeile -> Weltkoordinate -> geografische Breite -> Rasterzeile.
    // Genau hier steckt die Umprojektion: gleichmaessige Schritte in der
    // Mercator-Welt entsprechen ungleichen Schritten in der Breite.
    const worldY = topLeft.y + ((y + 0.5) / height) * worldHeight;
    const lat = toLatLon({ x: 0, y: worldY }).lat;

    const gridRow = ((north - lat) / (north - south)) * (gridHeight - 1);
    const row0 = Math.max(0, Math.min(gridHeight - 1, Math.floor(gridRow)));
    const row1 = Math.min(gridHeight - 1, row0 + 1);
    const blend = gridRow - row0;

    for (let x = 0; x < width; x++) {
      const upper = bathy.elevationAtIndex(x, row0);
      const lower = bathy.elevationAtIndex(x, row1);
      currentRow[x] = upper + (lower - upper) * blend;
    }

    for (let x = 0; x < width; x++) {
      const elevation = currentRow[x] as number;
      const [baseR, baseG, baseB] = colourFor(elevation);
      const index = (y * width + x) * 4;

      // Die Schummerung wird auf die Grundfarbe multipliziert. Der Sockel von
      // 0,72 verhindert, dass abgewandte Haenge ins Schwarz absaufen.
      const shade = 0.72 + 0.62 * reliefAt(bathy, x, row0);
      const r = Math.min(255, baseR * shade);
      const g = Math.min(255, baseG * shade);
      const b = Math.min(255, baseB * shade);

      pixels[index] = r;
      pixels[index + 1] = g;
      pixels[index + 2] = b;
      pixels[index + 3] = 255;

      const leftNeighbour = x > 0 ? (currentRow[x - 1] as number) : elevation;
      const aboveNeighbour = y > 0 ? (previousRow[x] as number) : elevation;

      if (crossesContour(elevation, leftNeighbour) || crossesContour(elevation, aboveNeighbour)) {
        // Aufhellung derselben Grundfarbe, damit die Linie zur Tiefe passt.
        contourPixels[index] = r + (255 - r) * CONTOUR_LIGHTEN;
        contourPixels[index + 1] = g + (255 - g) * CONTOUR_LIGHTEN;
        contourPixels[index + 2] = b + (255 - b) * CONTOUR_LIGHTEN;
        contourPixels[index + 3] = 255;
      }
    }

    const swap = previousRow;
    previousRow = currentRow;
    currentRow = swap;
  }

  baseContext.putImageData(baseImage, 0, 0);
  contourContext.putImageData(contourImage, 0, 0);

  return {
    base: baseCanvas,
    contours: contourCanvas,
    world: { x: topLeft.x, y: topLeft.y, width: worldWidth, height: worldHeight },
  };
}
